import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { motion, AnimatePresence } from 'framer-motion';
import { initializeGA, disableGA } from '@/lib/analytics';

const COOKIE_CONSENT_KEY = 'cookie-consent';

const CookieBanner = () => {
  const [visible, setVisible] = useState(false);
  const [showCustomize, setShowCustomize] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);

  useEffect(() => {
    const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!consent) {
      setVisible(true);
    }
  }, []);

  const handleConsent = (type: 'all' | 'rejected' | 'custom') => {
    if (type === 'all') {
      localStorage.setItem(COOKIE_CONSENT_KEY, 'all');
      initializeGA();
    } else if (type === 'custom') {
      const value = analytics ? 'custom-analytics' : 'custom-none';
      localStorage.setItem(COOKIE_CONSENT_KEY, value);
      if (analytics) {
        initializeGA();
      } else {
        disableGA();
      }
    } else {
      localStorage.setItem(COOKIE_CONSENT_KEY, 'rejected');
      disableGA();
    }
    setVisible(false);
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ duration: 0.4, ease: 'easeOut' }}
          className="fixed bottom-0 left-0 right-0 z-[9999] p-4 md:p-6"
        >
          <div className="mx-auto max-w-2xl rounded-2xl border border-border bg-card p-6 shadow-lg">
            <h3 className="text-lg font-semibold tracking-normal mb-2 text-foreground" style={{ fontFamily: "'Inter', system-ui, sans-serif" }}>
              Wir respektieren Ihre Privatsphäre
            </h3>
            <p className="text-sm text-muted-foreground leading-relaxed mb-1">
              Wir verwenden Cookies, um Ihr Nutzererlebnis zu verbessern. Mit Klick auf „Alle akzeptieren" stimmen Sie der Verwendung aller Cookies zu.{' '}
              <Link to="/datenschutz" className="underline text-foreground hover:text-primary transition-colors">
                Datenschutzerklärung
              </Link>
            </p>

            <AnimatePresence>
              {showCustomize && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  className="overflow-hidden"
                >
                  <div className="mt-4 space-y-3 border-t border-border pt-4">
                    <label className="flex items-center gap-3 text-sm text-foreground">
                      <input type="checkbox" checked disabled className="accent-primary h-4 w-4" />
                      <span>Notwendig <span className="text-muted-foreground">(immer aktiv)</span></span>
                    </label>
                    <label className="flex items-center gap-3 text-sm text-foreground cursor-pointer">
                      <input type="checkbox" checked={analytics} onChange={() => setAnalytics(!analytics)} className="accent-primary h-4 w-4" />
                      Analyse
                    </label>
                    <label className="flex items-center gap-3 text-sm text-foreground cursor-pointer">
                      <input type="checkbox" checked={marketing} onChange={() => setMarketing(!marketing)} className="accent-primary h-4 w-4" />
                      Marketing
                    </label>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            <div className="mt-5 flex flex-wrap items-center gap-3">
              <button
                onClick={() => setShowCustomize(!showCustomize)}
                className="text-sm font-semibold text-muted-foreground hover:text-foreground transition-colors"
              >
                Anpassen
              </button>
              <button
                onClick={() => handleConsent('rejected')}
                className="text-sm font-semibold text-muted-foreground hover:text-foreground transition-colors ml-auto md:ml-4"
              >
                Alle ablehnen
              </button>
              {showCustomize && (
                <Button
                  variant="outline"
                  onClick={() => handleConsent('custom')}
                  className="ml-2"
                >
                  Auswahl akzeptieren
                </Button>
              )}
              <Button
                onClick={() => handleConsent('all')}
                className={showCustomize ? '' : 'ml-2'}
              >
                Alle akzeptieren
              </Button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default CookieBanner;
